<template>
    <div class="support-ticket-list">
        <div class="list-header">
            <div class="header-title">MY TICKETS</div>
            <button class="button-create" @click="createTicket" :disabled="socketSendLoading !== null">
                + CREATE TICKET
            </button>
        </div>
        
        <div class="list-content">
            <transition name="fade" mode="out-in">
                <div v-if="supportTickets.loading" class="list-loading" key="loading">
                    <div v-for="index in 3" :key="index" class="loading-item">
                        <div class="loading-subject"></div>
                        <div class="loading-meta"></div>
                    </div>
                </div>
                <div v-else-if="supportTickets.data === null || supportTickets.data.length === 0" class="list-empty" key="empty">
                    <div class="empty-text">No tickets yet. Create your first ticket!</div>
                </div>
                <div v-else class="list-items" key="list">
                    <div 
                        v-for="ticket in supportTickets.data" 
                        :key="ticket._id" 
                        class="ticket-item"
                        :class="{ 
                            'ticket-open': ticket.status === 'open',
                            'ticket-in-progress': ticket.status === 'in-progress',
                            'ticket-resolved': ticket.status === 'resolved',
                            'ticket-closed': ticket.status === 'closed',
                            'ticket-selected': supportSelectedTicket === ticket._id
                        }"
                        @click="selectTicket(ticket._id)"
                    >
                        <div class="item-header">
                            <div class="item-subject">{{ ticket.subject }}</div>
                            <div class="item-priority" :class="'priority-' + ticket.priority">{{ ticket.priority.toUpperCase() }}</div>
                        </div>
                        <div class="item-meta">
                            <span class="meta-status" :class="'status-' + ticket.status">{{ getStatusLabel(ticket.status) }}</span>
                            <span class="meta-time">{{ formatTime(ticket.updatedAt) }}</span>
                        </div>
                    </div>
                </div>
            </transition>
        </div>
        
        <div v-if="supportTickets.pages > 1" class="list-pagination">
            <button 
                class="button-prev" 
                @click="changePage(supportTickets.page - 1)" 
                :disabled="supportTickets.page <= 1"
            >
                PREV
            </button>
            <span class="pagination-info">{{ supportTickets.page }} / {{ supportTickets.pages }}</span>
            <button 
                class="button-next" 
                @click="changePage(supportTickets.page + 1)" 
                :disabled="supportTickets.page >= supportTickets.pages"
            >
                NEXT
            </button>
        </div>
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

export default {
    name: 'SupportTicketList',
    methods: {
        ...mapActions([
            'supportSetView',
            'supportSetSelectedTicket',
            'supportGetTicketsSocket',
            'supportGetTicketDetailSocket'
        ]),
        createTicket() {
            this.supportSetView('create');
        },
        selectTicket(ticketId) {
            this.supportSetSelectedTicket(ticketId);
            this.supportGetTicketDetailSocket({ ticketId });
            this.supportSetView('detail');
        },
        changePage(page) {
            if(page < 1 || page > this.supportTickets.pages) { return; }
            this.supportGetTicketsSocket({ page });
        },
        formatTime(timestamp) {
            const date = new Date(timestamp);
            const now = new Date();
            const diffMs = now - date;
            const diffMins = Math.floor(diffMs / 60000);
            const diffHours = Math.floor(diffMs / 3600000);
            const diffDays = Math.floor(diffMs / 86400000);

            if (diffMins < 1) {
                return 'Just now';
            } else if (diffMins < 60) {
                return `${diffMins}m ago`;
            } else if (diffHours < 24) {
                return `${diffHours}h ago`;
            } else if (diffDays < 7) {
                return `${diffDays}d ago`;
            } else {
                return date.toLocaleDateString();
            }
        },
        getStatusLabel(status) {
            const labels = {
                'open': 'Open',
                'in-progress': 'In Progress',
                'resolved': 'Resolved',
                'closed': 'Closed'
            };
            return labels[status] || status;
        }
    },
    computed: {
        ...mapGetters([
            'supportTickets',
            'supportSelectedTicket',
            'socketSendLoading'
        ])
    },
    mounted() {
        this.supportGetTicketsSocket({ page: 1 });
    }
}
</script>

<style scoped>
.support-ticket-list {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
}

.list-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 15px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.header-title {
    font-size: 16px;
    font-weight: 800;
    color: #ffffff;
}

.button-create {
    padding: 8px 16px;
    background: var(--gradient-button-bg);
    border: none;
    border-radius: 6px;
    color: #ffffff;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.3s ease;
}

.button-create:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(1, 225, 164, 0.3);
}

.button-create:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.list-content {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 10px;
}

.list-loading {
    width: 100%;
}

.loading-item {
    padding: 12px;
    margin-bottom: 10px;
    background: rgba(255, 255, 255, 0.05);
    border-radius: 8px;
}

.loading-subject {
    width: 70%;
    height: 16px;
    background: #2A2A2A;
    border-radius: 4px;
    margin-bottom: 8px;
}

.loading-meta {
    width: 40%;
    height: 12px;
    background: #2A2A2A;
    border-radius: 4px;
}

.list-empty {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    padding: 40px;
}

.empty-text {
    font-size: 14px;
    color: #80889A;
    text-align: center;
}

.ticket-item {
    padding: 12px;
    margin-bottom: 8px;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.3s ease;
}

.ticket-item:hover {
    background: rgba(255, 255, 255, 0.08);
    border-color: var(--accent-yellow);
}

.ticket-item.ticket-selected {
    background: rgba(1, 225, 164, 0.1);
    border-color: var(--accent-yellow);
}

.item-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
}

.item-subject {
    font-size: 14px;
    font-weight: 700;
    color: #ffffff;
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.item-priority {
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 10px;
    font-weight: 700;
    margin-left: 10px;
}

.priority-low {
    background: rgba(59, 126, 183, 0.3);
    color: #3b7eb7;
}

.priority-medium {
    background: rgba(255, 204, 0, 0.3);
    color: #ffcc00;
}

.priority-high {
    background: rgba(255, 68, 68, 0.3);
    color: #ff4444;
}

.item-meta {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 12px;
}

.meta-status {
    padding: 3px 8px;
    border-radius: 4px;
    font-weight: 600;
}

.status-open {
    background: rgba(1, 225, 164, 0.2);
    color: #01e1a4;
}

.status-in-progress {
    background: rgba(255, 204, 0, 0.2);
    color: #ffcc00;
}

.status-resolved {
    background: rgba(59, 126, 183, 0.2);
    color: #3b7eb7;
}

.status-closed {
    background: rgba(128, 136, 154, 0.2);
    color: #80889A;
}

.meta-time {
    color: #80889A;
}

.list-pagination {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 15px;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.button-prev,
.button-next {
    padding: 8px 16px;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 6px;
    color: #ffffff;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
}

.button-prev:hover:not(:disabled),
.button-next:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.1);
    border-color: var(--accent-yellow);
}

.button-prev:disabled,
.button-next:disabled {
    opacity: 0.3;
    cursor: not-allowed;
}

.pagination-info {
    font-size: 12px;
    color: #80889A;
    font-weight: 600;
}
</style>

